define((require) => {
    "use strict";

    var EmailUtil        = require("util/EmailUtil"),
        Cookie           = require("editor/util/Cookie"),
        Login            = require("security/Login"),
        SequentialButton = require("util/SequentialButton"),
        ThrottleView     = require("ui/common/ThrottleView"),
        ViewController   = require("editor/util/ViewController");

    // Cookie names...
    var TERMS_OF_USE_SHOWN = "termsOfUseShown";

    function getUrlHashParam(key) {
        const hash = window.location.hash;
        if (hash.indexOf(key) !== -1) {
            return hash.split("&").find((fragment) => fragment.indexOf(key) !== -1).split("=")[1];
        }
    }

    /**
     * <tt>ViewController</tt> for the SignInView. Handles the actual sign in process.
     *
     * @class SignInViewController
     * @see  SignInView
     *
     * @author Kim
     */
    return ViewController.extend({

        /**
         * @private @type {SequentialButton}
         * The sequential button used to submit the login form.
         */
        _action: null,
        /**
         * @private @type {ThrottleView}
         * The throttle view handling lockout and displaying lockout messages for too many login attempts.
         */
        _throttleView: null,
        /**
         * @private
         * @type {Promise}
         * The promise to resolve on successful sign in.
         */
        _loginDeferred: null,
        /**
         * @private
         * @type {Promise}
         * The message that should be displayed if there is a sign-in error.
         */
        _errorMessage: null,

        /**
         * @private
         * Performs a login with the current entered credentials, returning a promise that on success will resolve
         * to the logged in principal. <p>
         *
         * On too many failed password attempts within a given time frame, specified by the backend, the user will be
         * locked out from submitting more login requests. After the lockout time expires, she will be allowed to
         * try again.
         *
         * @return {Promise|Boolean} A promise the will resolve to the logged in user; never null, or false if no
         *                           login was attempted because of missing username/password.
         */
        _login: function() {
            // Make sure the form is valid, i.e. valid email and non-blank password...
            if (!this.view.inputValidation()) {
                return false;
            }

            // Remove shake effect for any previous submitted request...
            this.view.shake(false);

            var email = this.view.ui.emailInput.val();

            // Do the login...
            var promise = Login.loginWithPassword(email, this.view.ui.passwordInput.val(), this.view.ui.persistentInput.prop("checked"))
                .done((principal) => {
                    Cookie.set(TERMS_OF_USE_SHOWN, true);
                    this._loginDeferred.resolve(principal);
                })

                .fail(() => {
                    this.view.shake(); // Shake it baby...
                });

            // Check the response for throttling status code...
            return this._throttleView.checkPromise(email, promise);
        },

        /**
         * @private
         * Performs a login using the JWT response from Auth0 (if available), returning a promise that on success will resolve
         * to the logged in principal. <p>
         *
         * @return {Promise|Boolean} A promise that will resolve to the logged in user; never null, or false if no
         *                           login was attempted because of missing username/password.
         */
        _handleSsoResponse: function() {
            const jsonWebToken = getUrlHashParam("access_token");
            if (jsonWebToken) {
                return Login.loginWithSSO(jsonWebToken)
                    .done((principal) => {
                        this._loginDeferred.resolve(principal);
                    })
                    .fail(() => {
                        this.view.showErrorMessage("User account is not registered<br />Please reach out to support@zmags.com");
                        history.pushState(null, null, " ");
                    });
            }

            // Handle SSO error from Auth0 (from URL hash)
            const errorStatus = getUrlHashParam("error");
            if (errorStatus) {
                const errorDescription = getUrlHashParam("error_description");

                const decodedErrorStatus = decodeURIComponent(errorStatus);
                const decodedErrorDescription = errorDescription ? decodeURIComponent(errorDescription) : "";

                this._errorMessage = `SSO Error - ${decodedErrorStatus}<br />${decodedErrorDescription}<br />Please reach out to support@zmags.com`;
            }
        },

        onInitialize: function() {
            this._loginDeferred = this.options.loginDeferred;

            this._handleSsoResponse();
        },

        onViewRender: function() {
            // Construct the login callback using the SequentialButton...
            this._action = SequentialButton.add(this.view.ui.signInButton, this._login.bind(this), true, true);

            // Throttle view...
            this._throttleView = new ThrottleView({
                name:     "login",
                lockout:  "You have entered invalid credentials too many times!",
                target:   this.view.ui.emailInput, // entered email = target
                validate: EmailUtil.validateEmail, // make sure we only throttle for valid emails, will not submit otherwise
                button:   this._action             // disable button during lockout
            });
            this.view.throttleRegion.show(this._throttleView);

            // Hide terms of use if already shown on successful login...
            if (Cookie.get(TERMS_OF_USE_SHOWN)) {
                this.view.showTermsOfUse(false);

            // Hide terms of use if not already shown and lockout is in progress - not much room on the page...
            } else {
                this.listenTo(this._throttleView, "lockout", function(lockout) {
                    this.view.showTermsOfUse(!lockout);
                });
            }

            // check if there was a sign-in error and render it
            if (this._errorMessage) {
                this.view.showErrorMessage(this._errorMessage);
            }
        },

        onViewSubmit: function() { // from SignInView SUBMIT event
            this._action();
        }

    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/SignInViewController.js